import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Button,
  NMPopup,
  Input,
  NMWaitIndicator
} from "@neumetric/blueprint-lib/components";
import { useDispatch, useSelector } from "react-redux";
import {
  auditorActions,
  getModelList,
  getModelDetails,
  getModelDetailsLoading,
  getModelDetailsError,
} from "../../../../store";
import { AllocateBlueprintPopup } from "./AllocatePopup";
import { ActivateDeactivatePopup } from "./ActivateDeactive";

export const ViewAuditBlueprintScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { blueprintId } = useParams();
  const modelList = useSelector(getModelList);
  const details = useSelector(getModelDetails);
  const loading = useSelector(getModelDetailsLoading);
  const error = useSelector(getModelDetailsError);

  const [state, setState] = React.useState({
    showAllocatePopup: false,
    openActivateDeactivePopup: false,
    activateDeactivateMode: "deactivate",
  });

  const model = React.useMemo(() => {
    return (modelList || []).find((x) => x.guid === blueprintId);
  }, [modelList, blueprintId]);

  const modelId = model?.id;
  const isInactive = String(details?.status || "").toLowerCase() === "inactive";

  // ensure modelList exists (if user landed directly on /A/:guid)
  React.useEffect(() => {
    if (!modelList || modelList.length === 0) {
      dispatch(auditorActions.fetchModelList());
    }
  }, [dispatch]);

  // once modelList is present, fetch details using id
  React.useEffect(() => {
    if (!modelId) {
      dispatch(auditorActions.setModelDetailsError("Model not found"));
      return;
    }
    dispatch(auditorActions.fetchModelDetails({ id: modelId }));
  }, [blueprintId, modelList]);

  // ----------------------------  popup handlers ----------------------------  //
  const updateState = (patch) => setState((_) => ({ ..._, ...patch }));

  const openAllocatePopup = () => updateState({ showAllocatePopup: true });

  const closeAllocatePopup = () =>
    updateState({
      showAllocatePopup: false,
    });

  const openDeactivatePopup = () =>
    updateState({
      openActivateDeactivePopup: true,
      activateDeactivateMode: "deactivate",
    });
  const openReactivatePopup = () =>
    updateState({
      openActivateDeactivePopup: true,
      activateDeactivateMode: "reactivate",
    });
  const closeActivateDeactivatePopup = () =>
    updateState({
      openActivateDeactivePopup: false,
    });

  return (
    <div className="pl-30 pr-30 pt-20 w-100">
      <Row
        className="text-action fs-14"
        onClick={() => navigate("/A/M")}
        style={{ cursor: "pointer" }}
      >
        Tracker
      </Row>
      {
        !loading &&
        <Row className="gap-20 mt-20" align="center" justify="start">
          {isInactive ? (
            <Button
              icon="CANCEL"
              label="Reactivate"
              variant="CAUTION"
              onClick={openReactivatePopup}
            />
          ) : (
            <>
              <Button
                icon="EDIT"
                label="Edit"
                onClick={() => navigate(`/A/M/${blueprintId}/edit`)}
              />
              <Button
                icon="ASSIGN"
                label="Allocate"
                onClick={openAllocatePopup}
              />
              <Button
                icon="CANCEL"
                label="Deactivate"
                variant="CAUTION"
                onClick={openDeactivatePopup}
              />
            </>
          )}
        </Row>
      }

      {loading && <Row className="mt-20 ff-exo2 w-100">
        <NMWaitIndicator size="small" />
      </Row>}
      {!!error && !loading && (
        <div className="mt-20 ff-exo2 text-danger">{error}</div>
      )}

      {!loading && !error && details && (
        <div className="w-100 mt-30 pl-20">
          <Row
            className="w-60"
            align="baseline"
            justify="between"
            style={{ gap: 20 }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                minWidth: 0,
                alignItems: "baseline",
              }}
            >
              <span
                className="ff-exo2 fs-16 fw-700 text-filled"
                style={{ minWidth: 95 }}
              >
                Title:
              </span>
              <span
                className="ff-exo2 fs-16 fw-600 text-filled"
                style={{ overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {details?.title ?? ""}
              </span>
            </div>

            <span className="ff-exo2 fs-18 fw-600 text-filled">
              Controls: {details?.controls ?? ""}
            </span>
          </Row>

          <Row className="mt-35 w-100 gap-20" align="start" justify="start">
            <div className="w-60">
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <span
                  className="ff-exo2 fs-16 fw-600 text-filled"
                  style={{ minWidth: 95 }}
                >
                  Description:
                </span>
                <div
                  className="ff-exo2 fs-16 fw-400 text-filled"
                  style={{
                    lineHeight: "22px",
                    maxWidth: 900,
                    whiteSpace: "pre-line",
                  }}
                >
                  {details?.desc ?? ""}
                </div>
              </div>
            </div>
            <Col
              className="w-20"
              style={{
                width: 440,
                height: 276,
                backgroundColor: "#f3f5f7", // fallback background
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {Boolean(details?.icon || details?.icone) ? (
                <img
                  src={details?.icon || details?.icone}
                  alt={details?.icon_file_name || "Blueprint icon"}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <span className="ff-exo2 fs-12 text-placeholder">No image</span>
              )}
            </Col>
          </Row>
        </div>
      )}
      {/*Allocate Popup*/}
      <AllocateBlueprintPopup
        visible={state.showAllocatePopup}
        modelId={modelId}
        onClose={closeAllocatePopup}
      />

      {/* Activate-Deactivate Popup */}
      <ActivateDeactivatePopup
        visible={state.openActivateDeactivePopup}
        mode={state.activateDeactivateMode}
        modelId={modelId}
        onClose={closeActivateDeactivatePopup}
      // onSuccessNavigate={state.mode === "deactivate" ? () => navigate("/A") : null}
      />
    </div>
  );
};
